import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { passwordService } from "../../../services/auth/password.service";
import { AxiosError } from "axios";
import { ArenaLogo } from "@/components/common/ArenaLogo";
import { EyeIcon, EyeOffIcon } from "lucide-react";

interface ApiErrorResponse {
  success: boolean;
  message: string;
}

export const BrandResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!resetToken) {
      navigate("/auth/login");
    }
  }, [resetToken, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);

    try {
      await passwordService.resetPassword(resetToken, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate("/auth/login");
      }, 2000);
    } catch (err) {
      if (err instanceof AxiosError) {
        const errorData = err.response?.data as ApiErrorResponse;
        setError(errorData?.message || "Failed to reset password");
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderError = () =>
    error && (
      <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg">{error}</div>
    );

  const renderSuccess = () =>
    success && (
      <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg">
        Password has been reset successfully. Redirecting to login...
      </div>
    );

  return (
    <div className=" md:m-auto mx-auto w-[98%] md:w-1/4 pt-4 md:pt-0 md:h-max">
      <div className="  h-full mx-auto flex flex-col items-center justify-center   md:py-7 py-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]  ">
         <ArenaLogo className="w-12 mb-8 border border-black mx-auto rounded-[12px] bg-[#f6f6f6]" />
      <div className="w-full flex items-center justify-center px-6">
        <div className="w-full">
          {renderError()}
          {renderSuccess()}
          <div className="mb-8">
            <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center align-middle">
              Set New Password
            </h1>
            <p className="font-normal text-[14px] mt-4 tracking-normal text-center align-middle text-muted-foreground">
              Please enter your new password below
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-muted-foreground"
              >
                New Password
              </label>
              <div className="relative mt-1">
                <input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="block w-full px-3 py-2 border border-border rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeIcon className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-muted-foreground"
              >
                Confirm New Password
              </label>
              <div className="relative mt-1">
                <input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="block w-full px-3 py-2 border border-border rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                  required
                  minLength={8}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin mr-2" />
              ) : null}
              <span>Reset Password</span>
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/auth/login"
              className="text-sm text-brand-600 hover:text-brand-500 font-medium"
            >
              Back to Login
            </Link>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
